// Belege von Hand: Rechnungen und Lieferscheine anlegen, als PDF holen, versenden.
//
// Angelegt wird IMMER über /api/documents — nie direkt gegen die Tabelle. Zwei
// Gründe: die Summen müssen serverseitig gerechnet werden (was der Browser
// schickt, sind Positionen, nicht Beträge), und das PDF entsteht im selben
// Zug. Ein Beleg ohne PDF wäre ein halber Beleg.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { BelegPosition } from "@/lib/beleg-summen";

export type BelegTyp = "rechnung" | "lieferschein";

export interface Lieferschein {
  id: string;
  slip_number: string | null;
  customer_id: string | null;
  order_id: string | null;
  pdf_url: string | null;
  signature_url: string | null;
  sent_at: string | null;
  notes: string | null;
  delivered_at: string;
  customer?: { display_name: string; customer_number: string | null } | null;
}

/** Aus der Tour oder von Hand? Das ist der Unterschied, den man sehen will. */
export function istHandbeleg(s: Lieferschein) {
  return !s.order_id;
}

export function useLieferscheine() {
  return useQuery({
    queryKey: ["lieferscheine"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("delivery_slips")
        .select("*, customer:customers(display_name, customer_number)")
        .order("delivered_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      return (data ?? []) as unknown as Lieferschein[];
    },
  });
}

async function ruf(body: Record<string, unknown>) {
  const { data: s } = await supabase.auth.getSession();
  const token = s.session?.access_token;
  if (!token) throw new Error("Nicht angemeldet");

  const res = await fetch("/api/documents", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error ?? `Fehler ${res.status}`);
  return j as { ok: true; id: string; nummer: string; pdf_url: string; brutto_cents?: number; an?: string };
}

export interface NeuerBeleg {
  typ: BelegTyp;
  customer_id: string;
  positionen: BelegPosition[];
  versand_cents?: number;
  notiz?: string | null;
  faellig_in_tagen?: number;
  bezahlt?: boolean;
}

export function useBelegErstellen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: NeuerBeleg) => ruf({ action: "create", ...b }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rechnungen"] });
      qc.invalidateQueries({ queryKey: ["rechnungen-kunde"] });
      qc.invalidateQueries({ queryKey: ["lieferscheine"] });
      qc.invalidateQueries({ queryKey: ["kassenlage"] });
      qc.invalidateQueries({ queryKey: ["zahlungsstand"] });
    },
  });
}

export function useBelegSenden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { typ: BelegTyp; id: string; an?: string }) => ruf({ action: "send", ...v }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rechnungen"] });
      qc.invalidateQueries({ queryKey: ["lieferscheine"] });
    },
  });
}

export function useBelegStornieren() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { typ: BelegTyp; id: string }) => ruf({ action: "storno", ...v }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rechnungen"] });
      qc.invalidateQueries({ queryKey: ["lieferscheine"] });
      qc.invalidateQueries({ queryKey: ["kassenlage"] });
    },
  });
}

/** Kundensuche für die Belegerfassung — Name oder Kundennummer. */
export function useKundenSuche(suche: string) {
  return useQuery({
    queryKey: ["kunden-suche", suche],
    enabled: suche.trim().length >= 2,
    queryFn: async () => {
      const q = suche.trim();
      const { data, error } = await supabase
        .from("customers")
        .select("id, display_name, customer_number, email, addresses:customer_addresses(street, zip, city)")
        .or(`display_name.ilike.%${q}%,customer_number.ilike.%${q}%`)
        .limit(20);
      if (error) throw error;
      return (data ?? []) as unknown as {
        id: string;
        display_name: string;
        customer_number: string | null;
        email: string | null;
        addresses: { street: string; zip: string | null; city: string | null }[];
      }[];
    },
  });
}
